/**
 * API Endpoints
 *
 * Centralized source of truth for all API endpoint paths.
 * All keys are lowercase for consistency.
 * Use these constants instead of hardcoding paths in service files.
 *
 * Endpoint versioning:
 * - v1: Default API version (auth, members)
 * - v2: Newer endpoints (account, signup)
 *
 * Usage:
 * ```typescript
 * import { API_ENDPOINTS } from '@/api'
 *
 * // Static endpoints
 * const url = API_ENDPOINTS.auth.login
 *
 * // Dynamic endpoints with parameters
 * const url = API_ENDPOINTS.members.departments.retrieve(123)
 * ```
 */

// Helper type for ID parameter
type IdParam = string | number
const version1 = "v1"
const version2 = "v2"

export const API_ENDPOINTS = {
  // ==================== Authentication (v1) ====================
  auth: {
    /** POST - Initiate OAuth login flow */
    login: `${version2}/auth/login/`,
    /** GET - Handle OAuth callback from Auth0 */
    callback: `${version1}/auth/handle-callback/`,
    /** POST - Refresh access token using refresh token */
    refresh: `${version1}/auth/refresh/`,
    /** POST - Logout user and invalidate tokens */
    logout: `${version1}/auth/logout/`,
    /** GET - Get current auth status */
    status: `${version1}/auth/get-status/`,
    /** GET - Get current user profile */
    user: `${version1}/auth/get-user/`,
    /** POST - Direct credential login (Resource Owner Password Grant) */
    credentialLogin: `${version2}/auth/login-credentials/`,
    /** POST - Verify MFA one-time code to complete a credential login */
    credentialLoginMfa: `${version2}/auth/login-mfa/`,
    /** POST - Verify MFA recovery code to complete a credential login */
    credentialLoginRecovery: `${version2}/auth/login-recovery-code/`,
    /** POST - Request password reset email */
    forgotPassword: `${version1}/forgot-password/`,
    /** Session management endpoints */
    sessions: {
      /** GET - List all active sessions for the authenticated user */
      list: `${version1}/auth/sessions/`,
      /** DELETE - Revoke a single session by ID */
      revoke: (id: IdParam) => `${version1}/auth/sessions/${id}/`,
      /** POST - Revoke all sessions except the current one */
      revokeAll: `${version1}/auth/sessions/revoke-all/`,
    },
  },

  // ==================== Account Management (v2) ====================
  account: {
    /** GET - Get authenticated user details including account status, tier, AWS, and payment info */
    me: `${version2}/account/me/`,
    /** POST - Setup credit card payment method */
    creditCard: `${version2}/account/add-credit-card/`,
    /** POST - Setup AWS credentials */
    awsCredentials: `${version2}/account/add-aws-credentials/`,
    /** POST - Re-check already-stored AWS credentials against AWS (no re-entry required) */
    checkAwsCredentialsStatus: `${version2}/account/aws-credentials/check-status/`,
    /** DELETE - Remove AWS credentials */
    removeAwsCredentials: `${version2}/account/remove-aws-credentials/`,
    /** POST - Start IAM role setup (returns the External ID and the trust policy) */
    setupAwsRole: `${version2}/account/setup-aws-role/`,
    /** POST - Connect an AWS account through an IAM role ARN */
    connectAwsRole: `${version2}/account/connect-aws-role/`,
    /** POST - Change user password */
    changePassword: `${version1}/account/change-password/`,
    /** GET/POST - Organization cost threshold */
    organizationCostThreshold: `${version1}/account/organization-cost-threshold/`,
    /** PATCH - Update user profile fields (legacy) */
    updateProfile: `${version2}/account/me/`,
    /** POST - Update account profile with personal/business info */
    update: `${version1}/account/update/`,
    /** POST - Change subscription tier */
    changePlan: `${version2}/account/change-tier/`,
    /** Notification preferences endpoints */
    notificationPreferences: {
      /** GET - Retrieve notification preferences */
      get: `${version1}/account/notification-preferences/get/`,
      /** PATCH - Update notification preferences (partial) */
      patch: `${version1}/account/notification-preferences/patch/`,
      /** POST - Send a sample email and / or text message to the given address and number */
      sendSample: `${version1}/account/notification-preferences/send-sample/`,
      /** POST - Text a 6-digit verification code to a US phone number */
      verifyPhoneSend: `${version1}/account/notification-preferences/verify-phone/send/`,
      /** POST - Confirm the code texted to a phone number; responds with the updated preferences */
      verifyPhoneConfirm: `${version1}/account/notification-preferences/verify-phone/confirm/`,
    },
  },

  // ==================== User Registration (v2) ====================
  /** POST - Register new user account */
  signup: `${version2}/signup/`,

  // ==================== Passwords (v1) ====================
  passwords: {
    /** GET - Generate a secure random password */
    generate: `${version1}/passwords/generate/`,
  },

  // ==================== Members / Cloud Allocation (v1) ====================
  members: {
    /** Department management endpoints */
    departments: {
      /** GET - List all departments */
      list: `${version1}/members/departments/list/`,
      /** POST - Create a new department */
      create: `${version1}/members/departments/create/`,
      /** GET - Retrieve a specific department by ID */
      retrieve: (id: IdParam) => `${version1}/members/departments/${id}/retrieve/`,
      /** PUT - Update a department by ID */
      update: (id: IdParam) => `${version1}/members/departments/${id}/update/`,
      /** PATCH - Partially update a department by ID (used for settings-only edits by a department admin) */
      patch: (id: IdParam) => `${version1}/members/departments/${id}/patch/`,
      /** DELETE - Delete a department by ID (unassigns members/admin/resources) */
      delete: (id: IdParam) => `${version1}/members/departments/${id}/delete/`,
      /** POST - Deactivate a department (soft delete; unassigns members/admin/resources) */
      deactivate: (id: IdParam) => `${version1}/members/departments/${id}/deactivate/`,
      /** POST - Activate a department (requires an assigned admin) */
      activate: (id: IdParam) => `${version1}/members/departments/${id}/activate/`,
      /** POST - Assign admin to a department */
      assignAdmin: (id: IdParam) => `${version1}/members/departments/${id}/assign-admin/`,
      /** POST - Remove admin from a department */
      removeAdmin: (id: IdParam) => `${version1}/members/departments/${id}/remove-admin/`,
    },
    /** Member/Account management endpoints */
    accounts: {
      /** GET - List all members. Supports ?department=<id>|unassigned */
      list: `${version1}/members/accounts/list/`,
      /** POST - Create a new member */
      create: `${version1}/members/accounts/create/`,
      /** GET - Retrieve a specific member by ID */
      retrieve: (id: IdParam) => `${version1}/members/accounts/${id}/retrieve/`,
      /** PUT - Update a member by ID */
      update: (id: IdParam) => `${version1}/members/accounts/${id}/update/`,
      /** PATCH - Partially update a member (a department admin may change first/last name and notes only) */
      patch: (id: IdParam) => `${version1}/members/accounts/${id}/patch/`,
      /** DELETE - Delete a member by ID */
      delete: (id: IdParam) => `${version1}/members/accounts/${id}/delete/`,
      /** PATCH - Reactivate a deactivated member (Super Admin only) */
      activate: (id: IdParam) => `${version1}/members/accounts/${id}/activate/`,
      /** PATCH - Deactivate a member (Super Admin or own-department Admin) */
      deactivate: (id: IdParam) => `${version1}/members/accounts/${id}/deactivate/`,
      /** POST - Assign permissions to a member */
      assignPermissions: (id: IdParam) => `${version1}/members/accounts/${id}/assign_permissions/`,
      /** POST - Remove permissions from a member */
      removePermissions: (id: IdParam) => `${version1}/members/accounts/${id}/remove_permissions/`,
      /** POST - Assign this member to a department (409 if already assigned elsewhere) */
      assignDepartment: (id: IdParam) => `${version1}/members/accounts/${id}/assign-department/`,
      /** POST - Unassign this member from their current department */
      removeDepartment: (id: IdParam) => `${version1}/members/accounts/${id}/remove-department/`,
      /** POST - Perform bulk action on multiple members */
      bulkAction: `${version1}/members/accounts/bulk_action/`,
    },
  },

  // ==================== Billing (v1) ====================
  billing: {
    /** AWS account management endpoints */
    awsAccounts: {
      /** GET - List all AWS accounts */
      list: `${version1}/billing/aws-accounts/list/`,
      /** POST - Create a new AWS account link */
      create: `${version1}/billing/aws-accounts/create/`,
      /** GET - Retrieve a specific AWS account by ID */
      retrieve: (id: IdParam) => `${version1}/billing/aws-accounts/${id}/retrieve/`,
      /** PUT - Update an AWS account by ID */
      update: (id: IdParam) => `${version1}/billing/aws-accounts/${id}/update/`,
      /** DELETE - Delete an AWS account by ID */
      delete: (id: IdParam) => `${version1}/billing/aws-accounts/${id}/delete/`,
      /** POST - Trigger billing data sync for an AWS account */
      sync: (id: IdParam) => `${version1}/billing/aws-accounts/${id}/sync/`,
      /** POST - Validate AWS credentials for an account */
      validate: (id: IdParam) => `${version1}/billing/aws-accounts/${id}/validate/`,
    },
    /** Billing snapshot endpoints */
    snapshots: {
      /** GET - List all snapshots */
      list: `${version1}/billing/snapshots/list/`,
      /** GET - Retrieve a specific snapshot by ID */
      retrieve: (id: IdParam) => `${version1}/billing/snapshots/${id}/retrieve/`,
      /** GET - Get current month costs across all AWS accounts */
      currentMonth: `${version1}/billing/snapshots/get-current-month/`,
      /** GET - Get cost trend over a period of time */
      trend: `${version1}/billing/snapshots/get-trend/`,
    },
    /** Invoice endpoints */
    invoices: {
      /** GET - List all invoices */
      list: `${version1}/billing/invoices/list/`,
      /** POST - Create a new invoice */
      create: `${version1}/billing/invoices/create/`,
      /** GET - Retrieve a specific invoice by ID */
      retrieve: (id: IdParam) => `${version1}/billing/invoices/${id}/retrieve/`,
      /** PUT - Update an invoice by ID */
      update: (id: IdParam) => `${version1}/billing/invoices/${id}/update/`,
      /** DELETE - Delete an invoice by ID (draft only) */
      delete: (id: IdParam) => `${version1}/billing/invoices/${id}/delete/`,
      /** POST - Initiate payment for an invoice */
      initiatePayment: (id: IdParam) => `${version1}/billing/invoices/${id}/initiate_payment/`,
      /** GET - List pending invoices */
      pending: `${version1}/billing/invoices/get-pending/`,
      /** GET - List overdue invoices */
      overdue: `${version1}/billing/invoices/get-overdue/`,
    },
    /** Payment record endpoints (read-only) */
    payments: {
      /** GET - List all payment records */
      list: `${version1}/billing/payments/list/`,
      /** GET - Retrieve a specific payment record by ID */
      retrieve: (id: IdParam) => `${version1}/billing/payments/${id}/retrieve/`,
    },
    /** GET - Stored cost summary for the Dashboard (reads our database only, never AWS) */
    dashboardSummary: `${version1}/billing/dashboard/get-summary/`,
    /** POST - Trigger billing data sync for all/specific AWS accounts */
    dashboardSync: `${version1}/billing/dashboard/sync/`,
    /** Billing alert endpoints */
    alerts: {
      /** GET - List all alerts */
      list: `${version1}/billing/alerts/list/`,
      /** POST - Create a new alert */
      create: `${version1}/billing/alerts/create/`,
      /** GET - Retrieve a specific alert by ID */
      retrieve: (id: IdParam) => `${version1}/billing/alerts/${id}/retrieve/`,
      /** PUT - Update an alert by ID */
      update: (id: IdParam) => `${version1}/billing/alerts/${id}/update/`,
      /** DELETE - Delete an alert by ID */
      delete: (id: IdParam) => `${version1}/billing/alerts/${id}/delete/`,
      /** POST - Send a test notification for an alert */
      test: (id: IdParam) => `${version1}/billing/alerts/${id}/test/`,
    },
  },

  // ==================== UCS - Unified Cloud Shadowing (v1) ====================
  ucs: {
    /** GET - Get dashboard summary (was used by the Dashboard page, now onboarding-only; see dashboard.store.ts) */
    // dashboardSummary: `${version1}/ucs/dashboard/get-summary/`,
  },

  // ==================== Notifications (v1) ====================
  notifications: {
    /** GET - List notifications (paginated, optional ?is_read=true|false) */
    list: `${version1}/notifications/list/`,
    /** PATCH - Mark a single notification as read */
    markRead: (id: string) => `${version1}/notifications/${id}/read/`,
    /** PATCH - Mark all notifications as read */
    markAllRead: `${version1}/notifications/read-all/`,
    /** DELETE - Delete a single notification */
    delete: (id: string) => `${version1}/notifications/${id}/delete/`,
    /** DELETE - Delete all read notifications */
    deleteAllRead: `${version1}/notifications/delete-all/`,
  },

  // ==================== Cloud Allocation (v1) ====================
  cloudAllocation: {
    integrations: {
      /** GET - List all integrations */
      list: `${version1}/cloud-allocation/integrations/list/`,
      /** POST - Create a new integration */
      create: `${version1}/cloud-allocation/integrations/create/`,
      /** POST - Sync resources for an integration */
      syncResources: (id: IdParam) => `${version1}/cloud-allocation/integrations/${id}/sync-resources/`,
    },
    resources: {
      /** GET - List all synced resources */
      list: `${version1}/cloud-allocation/resources/list/`,
    },
    allocations: {
      /** GET - List all allocations */
      list: `${version1}/cloud-allocation/allocations/list/`,
      /** POST - Create a new allocation */
      create: `${version1}/cloud-allocation/allocations/create/`,
      /** DELETE - Remove an allocation by ID */
      delete: (id: IdParam) => `${version1}/cloud-allocation/allocations/${id}/delete/`,
      /** POST - Allocate multiple resources/targets at once */
      bulkAllocate: `${version1}/cloud-allocation/allocations/bulk-allocate/`,
    },
    logs: {
      /** GET - Audit log of allocation/release events. Supports ?department=, ?member=, ?action=, ?resource= */
      list: `${version1}/cloud-allocation/logs/list/`,
    },
  },

  // ==================== Cost Alerts (v1) ====================
  costAlerts: {
    /** GET - Paginated per-server cost alerts (search, filters, ordering); POST - Create a cost alert */
    root: `${version1}/cost-alerts/`,
    /** GET / PATCH / DELETE - One cost alert */
    detail: (id: IdParam) => `${version1}/cost-alerts/${id}/`,
    /** POST - Switch a cost alert on or off */
    toggleStatus: (id: IdParam) => `${version1}/cost-alerts/${id}/toggle-status/`,
    /** POST - Activate, deactivate or delete several cost alerts ({ alert_ids, action }) */
    bulkAction: `${version1}/cost-alerts/bulk-action/`,
    /** GET - Paginated cost alert breach history */
    history: `${version1}/cost-alerts/history/`,
    /** POST - Manually resolve/clear an active cost alert event */
    resolve: (id: IdParam) => `${version1}/cost-alerts/history/${id}/resolve/`,
    /** GET - Aggregated budget health statistics */
    statistics: `${version1}/cost-alerts/statistics/`,
  },

  // ==================== Performance monitoring policies (v1) ====================
  performanceAlerts: {
    /** GET - Paginated per-server monitoring policies; POST - Create a policy */
    root: `${version1}/performance-alerts/`,
    /** GET / PATCH / DELETE - One policy */
    detail: (id: IdParam) => `${version1}/performance-alerts/${id}/`,
  },

  // ==================== Health Check (v1) ====================
  /** GET - Backend health check (unauthenticated) */
  health: `${version1}/health/`,

  // ==================== AWS Connect (v1) ====================
  awsConnect: {
    /** Usage reporting endpoints */
    usage: {
      /** GET - Get monthly usage and cost report */
      monthlyReport: `${version1}/aws-connect/usage/get-monthly-report/`,
      /** GET - List all usage records */
      list: `${version1}/aws-connect/usage/list/`,
      /** GET - Retrieve a specific usage record by ID */
      retrieve: (id: IdParam) => `${version1}/aws-connect/usage/${id}/retrieve/`,
    },
    /** EC2 instance endpoints */
    instances: {
      /** GET - List all instances */
      list: `${version1}/aws-connect/instances/list/`,
      /** POST - Create a new instance */
      create: `${version1}/aws-connect/instances/create/`,
      /** GET - Retrieve a specific instance by ID */
      retrieve: (id: IdParam) => `${version1}/aws-connect/instances/${id}/retrieve/`,
      /** POST - Start an instance by ID */
      start: (id: IdParam) => `${version1}/aws-connect/instances/${id}/start/`,
      /** POST - Stop an instance by ID */
      stop: (id: IdParam) => `${version1}/aws-connect/instances/${id}/stop/`,
      /** POST - Reboot an instance by ID */
      reboot: (id: IdParam) => `${version1}/aws-connect/instances/${id}/reboot/`,
      /** POST - Terminate an instance by ID */
      terminate: (id: IdParam) => `${version1}/aws-connect/instances/${id}/terminate/`,
      /** PUT - Update an instance by ID */
      update: (id: IdParam) => `${version1}/aws-connect/instances/${id}/update/`,
      /** GET - Get CloudWatch metrics for an instance */
      getMetrics: (id: IdParam) => `${version1}/aws-connect/instances/${id}/get-metrics/`,
      /** GET - Get CloudWatch metrics for every instance visible to the Super Admin (dashboard widget) */
      dashboardMetrics: `${version1}/aws-connect/instances/dashboard-metrics/`,
      /** GET - Check live status of an instance */
      checkStatus: (id: IdParam) => `${version1}/aws-connect/instances/${id}/check-status/`,
      /** GET - Generate and download RDP file for an instance */
      generateRdpFile: (id: IdParam) => `${version1}/aws-connect/instances/${id}/generate-rdp-file/`,
    },
    /** Instance type endpoints */
    instanceTypes: {
      /** GET - List all available instance types */
      list: `${version1}/aws-connect/instance-types/list/`,
    },
    /** Region endpoints */
    regions: {
      /** GET - List all available AWS regions */
      list: `${version1}/aws-connect/regions/list/`,
    },
    /** GET - Resolve latest Amazon Linux 2023 AMI ID for a region (?region=us-east-1) */
    amiResolve: (region: string) => `${version1}/aws-connect/ami-id/resolve/?region=${region}`,
    /** Cost summary endpoint */
    costSummary: `${version1}/aws-connect/cost-summary/`,
    /** GET - AWS spend trend for the trailing N months (default 6), powers Dashboard's Spend Trend chart */
    spendTrend: `${version1}/aws-connect/spend-trend/`,
    /** GET - Per-service AWS cost breakdown for one calendar month (?month=YYYY-MM), powers the Dashboard's AWS Invoices detail view */
    spendTrendServiceBreakdown: (month: string) =>
      `${version1}/aws-connect/spend-trend/service-breakdown/?month=${month}`,
    /** Credits endpoint */
    credits: `${version1}/aws-connect/credits/`,
    /** Current month cost endpoint */
    currentMonthCost: `${version1}/aws-connect/instances/get-current-month-cost/`,
    /** Security group rule endpoints */
    securityRules: {
      /** GET - List all security group rules */
      list: `${version1}/aws-connect/security-rules/list/`,
      /** POST - Create a new security group rule */
      create: `${version1}/aws-connect/security-rules/create/`,
      /** GET - Retrieve a specific security group rule by ID */
      retrieve: (id: IdParam) => `${version1}/aws-connect/security-rules/${id}/retrieve/`,
      /** PUT - Update a security group rule by ID */
      update: (id: IdParam) => `${version1}/aws-connect/security-rules/${id}/update/`,
      /** DELETE - Delete a security group rule by ID */
      delete: (id: IdParam) => `${version1}/aws-connect/security-rules/${id}/delete/`,
      /** POST - Revoke a security group rule by ID */
      revoke: (id: IdParam) => `${version1}/aws-connect/security-rules/${id}/revoke/`,
    },
    /** Audit report endpoints */
    auditReports: {
      /** GET - List all audit reports */
      list: `${version1}/aws-connect/audit-reports/list/`,
      /** POST - Create a new audit report */
      create: `${version1}/aws-connect/audit-reports/create/`,
      /** GET - Retrieve a single audit report by ID */
      retrieve: (id: IdParam) => `${version1}/aws-connect/audit-reports/${id}/retrieve/`,
      /** GET - Export audit report as HTML */
      exportHtml: (id: IdParam) => `${version1}/aws-connect/audit-reports/${id}/export-html/`,
      /** GET - Export audit report as CSV */
      exportCsv: (id: IdParam) => `${version1}/aws-connect/audit-reports/${id}/export-csv/`,
    },
    /** Performance recommendation endpoints */
    performanceRecommendations: {
      /** GET - List all performance recommendations */
      list: `${version1}/aws-connect/performance-recommendations/list/`,
      /** POST - Sync performance recommendations from AWS */
      sync: `${version1}/aws-connect/performance-recommendations/sync/`,
      /** GET - Retrieve a single performance recommendation by ID */
      retrieve: (id: number) => `${version1}/aws-connect/performance-recommendations/${id}/retrieve/`,
      /** GET - Get AWS Compute Optimizer enrollment status */
      enrollmentStatus: `${version1}/aws-connect/performance-recommendations/enrollment-status/`,
    },
  },
} as const

/**
 * Type for API endpoints object
 */
export type ApiEndpoints = typeof API_ENDPOINTS
